/* eslint-disable no-setter-return */
(function () {
  if (window.Component) {
    return;
  }

  const media = window.matchMedia('(max-width: 767px)');

  class Component extends HTMLElement {
    static get propsCamelConventionEnabled() {
      return false;
    }

    constructor() {
      super();
      this.attachShadow({ mode: 'open', delegatesFocus: true });
      if (typeof this.template !== 'function') {
        throw new Error(this._errorMessage('TEMPLATE_METHOD'));
      }
      this.shadowRoot.appendChild(this.template().content.cloneNode(true));

      this.attachedEvents = [];
      this._bindHandlers();
      this._shorthandEvents(this.selectAll('*'));
      this._autoReflect();

      this.rexWCTag = /<(\w+-[\w-]+)/g;
    }

    disconnectedCallback() {
      this.detachAllEvents();
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (oldValue === newValue) {
        return;
      }

      const pascalName = this.caseConvention(name, 'pascal');
      const method = `_attribute${pascalName}`;
      if (typeof this[method] === 'function') {
        this[method](newValue, oldValue);
      }
    }

    attachEvent(els, eventType, fn) {
      if (els instanceof NodeList) {
        els = Array.from(els);
      }
      if (!Array.isArray(els)) {
        els = [els];
      }
      els.forEach((el) => {
        if (!el || !el.addEventListener) {
          return;
        }

        el.addEventListener(eventType, fn);
        this.attachedEvents.push({ el, eventType, fn });
      });
    }

    attachEvents(eventCollection) {
      eventCollection.forEach(({ el, eventType, fn }) => this.attachEvent(el, eventType, fn));
    }

    /**
     * Converts string to various case conventions.
     * @param {string} str The value to convert
     * @param {*} convention Possible values: camel, kebab, pascal
     * @returns {string}
     */
    caseConvention(str, convention) {
      if (typeof str !== 'string') {
        return str;
      }

      const rexWords = /[A-Z][a-z]+|[A-Z]+(?=[A-Z][a-z])|[A-Z]+|[a-z]+|[0-9]+/g;
      const words = str.match(rexWords) || [];
      const upperFirst = s => `${s.slice(0, 1).toUpperCase()}${s.slice(1).toLowerCase()}`;

      switch (convention) {
        case 'camel': {
          const camel = words.map((s, i) => (i === 0 ? s.toLowerCase() : upperFirst(s))).join('');
          return camel;
        }
        case 'kebab': {
          const kebab = words.map(s => s.toLowerCase()).join('-');
          return kebab;
        }
        case 'pascal': {
          const pascal = words.map(s => upperFirst(s)).join('');
          return pascal;
        }
        default: {
          return str;
        }
      }
    }

    /**
     * Alias for caseConvention()
     */
    convertCase(...args) {
      return this.caseConvention(...args);
    }

    detachAllEvents() {
      this.attachedEvents.forEach(({ el, eventType, fn }) => el.removeEventListener(eventType, fn));
      this.attachedEvents = [];
    }

    detachEvent(el, eventType, fn) {
      const isEvent = e => el === e.el && eventType === e.eventType && fn === e.fn;
      const index = this.attachedEvents.findIndex(e => isEvent(e));
      const event = this.attachedEvents.find(e => isEvent(e));

      if (!event) {
        return;
      }

      event.el.removeEventListener(event.eventType, event.fn);
      this.attachedEvents.splice(index, 1);
    }

    detachEvents(eventCollection) {
      eventCollection.forEach(({ el, eventType, fn }) => this.detachEvent(el, eventType, fn));
    }

    /**
     * Find child elements with  `[type="application/json"]` attribute and
     * returns them in an object with `data-json` or `id` as the key.
     * @param {Array} selectors Array of selectors to find DOM JSON in the document
     * @returns {Object}
     */
    domJson(selectors = []) {
      const elsJsons = selectors
        .map(selector => document.querySelector(`[type="application/json"]${selector}`))
        .concat([...this.querySelectorAll('[type="application/json"]')])
        .filter(Boolean);

      const result = elsJsons.reduce((obj, el) => {
        const json = JSON.parse(el.textContent);
        const key = el.getAttribute('data-json') || el.id || 'unnamed';
        if (!obj[key]) {
          obj[key] = [];
        }
        obj[key].push(json);
        return obj;
      }, {});

      return result;
    }

    async elementsDefined(extraWCTags = []) {
      if (!Array.isArray(extraWCTags)) {
        extraWCTags = [extraWCTags];
      }

      const innerWCTags = Array.from(this.template().innerHTML.matchAll(this.rexWCTag)).map(([_, tag]) => tag);
      const wcTags = [...new Set(innerWCTags.concat(extraWCTags))];

      try {
        await Promise.all(wcTags.map(tag => customElements.whenDefined(tag)));
      } catch (err) {
        console.error(err); // eslint-disable-line no-console
        return;
      }
    }

    initBool(bools) {
      this.booleans = bools;
    }

    initSlotted() {
      const els = [];

      this.slotted = { unnamed: [] };
      this.selectAll('slot').forEach((el) => {
        if (el.name) {
          const slotName = this.constructor.propsCamelConventionEnabled
            ? this.caseConvention(el.name, 'camel')
            : el.name;
          this.slotted[slotName] = el.assignedNodes();
          els.push(el);
        } else {
          this.slotted.unnamed = [...this.slotted.unnamed, ...el.assignedNodes()];
        }
      });

      this._shorthandEvents(els);
    }

    async pause(duration = 1) {
      await new Promise(r => setTimeout(r, duration));
    }

    ready() {
      this.removeAttribute('skeleton');
      this.isReady = true;
    }

    responsiveAttribute(value) {
      let [mobile, desktop] = String(value).split(',');
      mobile = mobile.trim();
      desktop = desktop ? desktop.trim() : mobile;

      value = this._isMobile ? mobile : desktop;

      return value;
    }

    select(s) {
      return this.shadowRoot.querySelector(s);
    }

    selectAll(s) {
      return this.shadowRoot.querySelectorAll(s);
    }

    selectArray(s) {
      return [...this.selectAll(s)];
    }

    /**
     * Find if there is textContent or childNodes in a slotted element
     *
     * ```js
     * const hasTextContent = !this.slotEmpty('propName', 'textContent');
     * const hasChildNodes = !this.slotEmpty('propName', 'childNodes');
     * const hasElement = !this.slotEmpty('propName', 'element');
     * ```
     * @param {string} slotName The name of property to check at `this.slotted[slotName]`
     * @param {string} [checkFor='element']
     * @returns {boolean}
     */
    slotEmpty(slotName, checkFor = 'element') {
      switch (checkFor) {
        case 'textContent':
          return !this.slotted?.[slotName]?.[0]?.textContent.trim();
        case 'childNodes':
          return !this.slotted?.[slotName]?.[0]?.hasChildNodes();
        default:
          return !this.slotted?.[slotName]?.[0];
      }
    }

    _autoReflect() {
      const attrs = this.constructor.observedAttributes;
      if (!Array.isArray(attrs)) {
        return;
      }

      const isBool = attrName => Array.isArray(this.booleans) && this.booleans.includes(attrName);

      attrs.forEach((attrName) => {
        const propName = this.constructor.propsCamelConventionEnabled
          ? this.caseConvention(attrName, 'camel')
          : attrName;
        Object.defineProperty(this, propName, {
          set: (value) => {
            if (String(value) === 'true' && isBool(attrName)) {
              return this.setAttribute(attrName, '');
            }
            if (String(value) === 'false' && isBool(attrName)) {
              return this.removeAttribute(attrName);
            }
            if (isBool(attrName)) {
              return this.removeAttribute(attrName);
            }
            this.setAttribute(attrName, value);
          },
          get: () => {
            let attrValue = this.getAttribute(attrName);
            if ((attrValue === '' || attrValue === 'true') && isBool(attrName)) {
              attrValue = true;
            }
            if ((attrValue === null || attrValue === 'false') && isBool(attrName)) {
              attrValue = false;
            }
            if (isBool(attrName) && typeof attrValue !== 'boolean') {
              attrValue = false;
            }
            return attrValue;
          },
        });
      });
    }

    _bindHandlers() {
      Object.getOwnPropertyNames(this.constructor.prototype)
        .filter(prop => prop.startsWith('_on'))
        .forEach(handler => (this[handler] = this[handler].bind(this)));
    }

    _errorMessage(type) {
      return {
        TEMPLATE_METHOD: 'To extend "Component", a `template()` method is required',
      }[type];
    }

    get _isMobile() {
      return media.matches;
    }

    _shorthandEvents(els) {
      els.forEach((el) => {
        Array.from(el.attributes)
          .filter(attr => attr.name[0] === '@')
          .filter(attr => typeof this[attr.value] === 'function')
          .forEach((attr) => {
            const eventType = attr.name.slice(1);
            const fn = this[attr.value].bind(this);
            this.attachedEvents.push({ el, eventType, fn });

            el.addEventListener(eventType, fn);
            el.removeAttribute(attr.name);
          });
      });
    }
  }
  window.Component = Component;
  /* istanbul ignore next */
  if (typeof module !== 'undefined') {
    module.exports = Component;
  }
})();
